import {Brand} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/brand.types';
import {LinksSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/links_section.types';
import {GetHeaderV2Response} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {
  COLORS,
  Z_INDEXES,
  desktopAndUp,
  largeScreens,
  tabletAndUp,
} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';
import {BrandPageGenderSwitch} from './BrandPageGenderSwitch';
import {BrandPageHeaderImage} from './BrandPageHeaderImage';
import {BrandPageHeaderLogo} from './BrandPageHeaderLogo';
import {BrandPageSubBrands} from './BrandPageSubBrands/BrandPageSubBrands';

const DESKTOP_GENDER_SWITCH_HEIGHT = 36;

export interface BrandShopHeaderViewProps {
  HeaderElements: React.ComponentType<{
    brand: Brand;
    linksSection: LinksSection;
  }>;
  landscape: boolean;
}

export const BrandShopHeaderView: React.FC<
  GetHeaderV2Response & BrandShopHeaderViewProps
> = ({
  HeaderElements,
  brand,
  landscape,
  linksSection,
  subBrands,
  targetGroups,
  targetGroupSwitch,
}) => {
  const hasGenderSwitch = targetGroups?.length >= 2;

  return (
    <>
      <Wrapper data-theme={landscape ? 'dark' : ''}>
        <BrandPageHeaderImage
          landscape={landscape}
          images={
            brand.type?.$case === 'images' ? brand.type.images : undefined
          }
        />

        <TopWrapper>
          <HeaderElements brand={brand.brand} linksSection={linksSection} />
        </TopWrapper>

        <HeaderContent>
          <StyledBrandPageHeaderLogo brand={brand} />

          <BottomWrapper data-has-gender-switch={hasGenderSwitch}>
            {landscape && (
              <StyledBrandPageGenderSwitch
                targetGroups={targetGroups}
                targetGroupSwitch={targetGroupSwitch}
                $landscape={landscape}
              />
            )}
            {subBrands && <BrandPageSubBrands subBrands={subBrands} />}
          </BottomWrapper>
        </HeaderContent>
      </Wrapper>
      {!landscape && (
        <StyledBrandPageGenderSwitch
          targetGroups={targetGroups}
          targetGroupSwitch={targetGroupSwitch}
          $landscape={landscape}
        />
      )}
    </>
  );
};

const Wrapper = styled.section`
  position: relative;
  display: flex;
  justify-content: center;
`;

const TopWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: space-between;
  width: calc(100% - 26px);
  top: 9px;
  z-index: ${Z_INDEXES.TADARIDA_BRANDS_HUB_HEADER_NAVIGATION_BUTTONS};

  ${tabletAndUp(
    css`
      width: calc(100% - 30px);
      top: 12px;
    `,
  )}

  ${desktopAndUp(css`
    width: calc(100% - 120px);
    top: 32px;
  `)}
`;

const HeaderContent = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  bottom: 0;

  ${tabletAndUp(
    css`
      bottom: 12px;
    `,
  )}

  ${desktopAndUp(
    css`
      bottom: 24px;
    `,
  )}
`;

const StyledBrandPageHeaderLogo = styled(BrandPageHeaderLogo)`
  margin-bottom: 24px;

  ${tabletAndUp(
    css`
      margin-bottom: 8px;
    `,
  )}

  ${largeScreens(css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    margin-bottom: 0;
  `)}
`;

const BottomWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
  margin-bottom: 24px;

  ${tabletAndUp(
    css`
      margin-top: ${DESKTOP_GENDER_SWITCH_HEIGHT / 2}px;
      margin-bottom: unset;
      gap: 8px;

      &[data-has-gender-switch='true'] {
        margin-top: 0;
      }
    `,
  )}

  ${desktopAndUp(
    css`
      gap: 24px;
    `,
  )}
`;

const StyledBrandPageGenderSwitch = styled(BrandPageGenderSwitch)<{
  $landscape: boolean;
}>`
  display: flex;
  justify-content: center;
  height: 42px;
  background: ${COLORS.White};
  border-bottom: 1px solid ${COLORS.Grey3};
  ${({$landscape}) =>
    $landscape &&
    css`
      height: ${DESKTOP_GENDER_SWITCH_HEIGHT}px;
      background: unset;
      border-bottom: unset;
    `}
`;
